import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { casesApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
    LayoutDashboard, 
    Users, 
    FolderOpen, 
    Plus, 
    LogOut,
    FileText,
    Clock,
    User,
    ChevronRight,
    AlertCircle,
    Building,
    Settings,
    Key,
    Eye,
    EyeOff,
    Check,
    ArrowUpDown,
    EyeOff as HideIcon,
    Shield
} from 'lucide-react';
import { getStatusColor, getStatusLabel, formatDate } from '../lib/utils';
import ThemeToggle from '../components/ThemeToggle';
import StyleSwitcher from '../components/StyleSwitcher';
import CreateCaseModal from '../components/CreateCaseModal';
import CreateUserModal from '../components/CreateUserModal';
import UserManagement from '../components/UserManagement';
import { authApi } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [cases, setCases] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('cases');
    const [showCreateCase, setShowCreateCase] = useState(false);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [error, setError] = useState('');
    
    // Filtri e ordinamento casi
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = più recenti prima, 'asc' = più vecchi prima
    const [hideClosed, setHideClosed] = useState(false);
    
    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [casesRes, usersRes] = await Promise.all([
                casesApi.list(),
                usersApi.list()
            ]);
            setCases(casesRes.data);
            setUsers(usersRes.data);
        } catch (err) {
            setError('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };

    // Filtra e ordina i casi
    const getFilteredAndSortedCases = () => {
        let filtered = [...cases];
        
        // Filtra casi chiusi se richiesto
        if (hideClosed) {
            filtered = filtered.filter(c => c.status !== 'closed');
        }
        
        // Ordina per data di creazione
        filtered.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        
        return filtered;
    };

    const toggleSortOrder = () => {
        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    const handleCaseCreated = (newCase) => {
        setCases([newCase, ...cases]);
        setShowCreateCase(false);
    };

    const handleUserCreated = (newUser) => {
        setUsers([...users, newUser]);
        setShowCreateUser(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        
        if (newPassword !== confirmPassword) {
            setPasswordError('Le password non coincidono');
            return;
        }
        
        if (newPassword.length < 6) {
            setPasswordError('La nuova password deve essere di almeno 6 caratteri');
            return;
        }
        
        try {
            setPasswordLoading(true);
            await authApi.changePassword(currentPassword, newPassword);
            setPasswordSuccess('Password aggiornata con successo!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPasswordError(err.response?.data?.detail || 'Errore nel cambio password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const stats = {
        totalCases: cases.length,
        openCases: cases.filter(c => c.status === 'open').length,
        totalAgents: users.filter(u => u.role === 'agent').length,
        activeAgents: users.filter(u => u.role === 'agent' && u.is_active).length
    };

    return (
        <div className="min-h-screen bg-[#050505]">
            {/* Noise overlay */}
            <div className="noise-overlay" />

            {/* Header */}
            <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <LayoutDashboard className="w-6 h-6 text-[#00FF41]" />
                                <span className="font-heading text-lg font-bold text-white">
                                    ADMIN PANEL
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-white/60 text-sm font-mono hidden sm:inline">
                                {user?.full_name}
                            </span>
                            <StyleSwitcher />
                            <ThemeToggle compact />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={logout}
                                data-testid="logout-btn"
                                className="text-white/60 hover:text-white hover:bg-white/10"
                            >
                                <LogOut className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Casi Totali</p>
                                <p className="text-2xl font-heading font-bold text-white">{stats.totalCases}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <Clock className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Casi Aperti</p>
                                <p className="text-2xl font-heading font-bold text-[#00FF41]">{stats.openCases}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <Users className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Agenti Totali</p>
                                <p className="text-2xl font-heading font-bold text-white">{stats.totalAgents}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-[#0A0A0A] border border-white/10 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                <User className="w-5 h-5 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white/60 text-xs uppercase tracking-wider">Agenti Attivi</p>
                                <p className="text-2xl font-heading font-bold text-[#00FF41]">{stats.activeAgents}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-4 mb-6">
                    <button
                        onClick={() => setActiveTab('cases')}
                        data-testid="tab-cases"
                        className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'cases'
                                ? 'bg-[#00FF41] text-black'
                                : 'bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-[#00FF41]/50'
                        }`}
                    >
                        <FolderOpen className="w-4 h-4 inline mr-2" />
                        Casi Investigativi
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        data-testid="tab-users"
                        className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'users'
                                ? 'bg-[#00FF41] text-black'
                                : 'bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-[#00FF41]/50'
                        }`}
                    >
                        <Users className="w-4 h-4 inline mr-2" />
                        Gestione Utenti
                    </button>
                    <button
                        onClick={() => navigate('/clients')}
                        data-testid="nav-clients"
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-[#00FF41]/50 hover:text-[#00FF41]"
                    >
                        <Building className="w-4 h-4 inline mr-2" />
                        Anagrafica Clienti
                    </button>
                    <button
                        onClick={() => navigate('/admin/security')}
                        data-testid="nav-security-events"
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-orange-400/50 hover:text-orange-400"
                        title="Eventi di sicurezza (login, accessi, rate limit)"
                    >
                        <Shield className="w-4 h-4 inline mr-2" />
                        Eventi Sicurezza
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        data-testid="tab-settings"
                        className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all ${
                            activeTab === 'settings'
                                ? 'bg-[#00FF41] text-black'
                                : 'bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-[#00FF41]/50'
                        }`}
                    >
                        <Settings className="w-4 h-4 inline mr-2" />
                        Impostazioni
                    </button>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500 p-4 mb-6 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="text-red-500">{error}</span>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                    </div>
                ) : activeTab === 'cases' ? (
                    <div>
                        {/* Action Bar */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-heading text-xl font-bold text-white">
                                Casi Investigativi
                            </h2>
                            <div className="flex items-center gap-3">
                                {/* Bottone Ordina */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={toggleSortOrder}
                                    data-testid="sort-cases-btn"
                                    className="bg-[#0A0A0A] border-white/20 text-white/80 hover:border-[#00FF41] hover:text-[#00FF41]"
                                >
                                    <ArrowUpDown className="w-4 h-4 mr-2" />
                                    {sortOrder === 'desc' ? 'Più recenti' : 'Più vecchi'}
                                </Button>
                                
                                {/* Bottone Nascondi Chiusi */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setHideClosed(!hideClosed)}
                                    data-testid="hide-closed-btn"
                                    className={`bg-[#0A0A0A] border-white/20 hover:border-[#00FF41] ${
                                        hideClosed 
                                            ? 'text-[#00FF41] border-[#00FF41]' 
                                            : 'text-white/80 hover:text-[#00FF41]'
                                    }`}
                                >
                                    {hideClosed ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                                    {hideClosed ? 'Mostra chiusi' : 'Nascondi chiusi'}
                                </Button>
                                
                                <Button
                                    onClick={() => setShowCreateCase(true)}
                                    data-testid="create-case-btn"
                                    className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    NUOVO CASO
                                </Button>
                            </div>
                        </div>

                        {/* Cases List */}
                        {getFilteredAndSortedCases().length === 0 ? (
                            <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
                                <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                <p className="text-white/60">
                                    {hideClosed && cases.length > 0 
                                        ? 'Nessun caso aperto. Premi "Mostra chiusi" per vedere tutti i casi.'
                                        : 'Nessun caso investigativo'
                                    }
                                </p>
                                {!hideClosed && (
                                    <Button
                                        onClick={() => setShowCreateCase(true)}
                                        className="mt-4 bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                                    >
                                        Crea il primo caso
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {getFilteredAndSortedCases().map((caseItem) => (
                                    <Link
                                        key={caseItem.id}
                                        to={`/cases/${caseItem.id}`}
                                        data-testid={`case-card-${caseItem.id}`}
                                        className="block bg-[#0A0A0A] border border-white/10 hover:border-[#00FF41]/50 transition-all p-4 group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41]/50 flex items-center justify-center">
                                                    <FolderOpen className="w-5 h-5 text-[#00FF41]" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-[#00FF41] text-sm">
                                                            {caseItem.protocol_number || caseItem.case_number}
                                                        </span>
                                                        <span className={`badge-neon ${getStatusColor(caseItem.status)}`}>
                                                            {getStatusLabel(caseItem.status)}
                                                        </span>
                                                    </div>
                                                    <p className="text-white font-medium mt-1">
                                                        {caseItem.client_name}
                                                    </p>
                                                    {caseItem.investigated?.name && (
                                                        <p className="text-white/60 text-sm">
                                                            Indagato: {caseItem.investigated.name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <p className="text-white/40 text-xs uppercase">Creato il</p>
                                                    <p className="text-white/80 font-mono text-sm">
                                                        {formatDate(caseItem.created_at)}
                                                    </p>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[#00FF41] transition-colors" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                ) : activeTab === 'users' ? (
                    <UserManagement 
                        users={users} 
                        onUserCreated={handleUserCreated}
                        onRefresh={loadData}
                    />
                ) : activeTab === 'settings' ? (
                    <div className="max-w-xl">
                        <h2 className="font-heading text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <Key className="w-5 h-5 text-[#00FF41]" />
                            Cambia Password
                        </h2>
                        
                        <div className="bg-[#0A0A0A] border border-white/10 p-6">
                            {passwordSuccess && (
                                <div className="bg-green-500/10 border border-green-500 p-3 mb-4 flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span className="text-green-500 text-sm">{passwordSuccess}</span>
                                </div>
                            )}
                            
                            {passwordError && (
                                <div className="bg-red-500/10 border border-red-500 p-3 mb-4 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                    <span className="text-red-500 text-sm">{passwordError}</span>
                                </div>
                            )}
                            
                            <form onSubmit={handleChangePassword} className="space-y-4">
                                <div>
                                    <Label className="text-white/60 text-xs uppercase">Password Attuale</Label>
                                    <div className="relative">
                                        <Input
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                            className="bg-black border-white/20 text-white pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                                        >
                                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="text-white/60 text-xs uppercase">Nuova Password</Label>
                                    <div className="relative">
                                        <Input
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            className="bg-black border-white/20 text-white pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                                        >
                                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="text-white/60 text-xs uppercase">Conferma Nuova Password</Label>
                                    <Input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="bg-black border-white/20 text-white"
                                    />
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={passwordLoading}
                                    className="w-full bg-[#00FF41] text-black hover:bg-[#00CC33]"
                                >
                                    {passwordLoading ? 'Aggiornamento...' : 'AGGIORNA PASSWORD'}
                                </Button>
                            </form>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Modals */}
            {showCreateCase && (
                <CreateCaseModal
                    agents={users.filter(u => u.role === 'agent' && u.is_active)}
                    onClose={() => setShowCreateCase(false)}
                    onCreated={handleCaseCreated}
                />
            )}
            {showCreateUser && (
                <CreateUserModal
                    onClose={() => setShowCreateUser(false)}
                    onCreated={handleUserCreated}
                />
            )}
        </div>
    );
}
